import { provide } from '@lit/context';
import { isAxiosError } from 'axios';
import { LitElement, PropertyValueMap } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, Ref } from 'lit/directives/ref.js';
import { ParsedQs } from 'qs';

import { stepNamedElements } from '@/data';

import {
  ColorCollapsibleStepDto,
  ColorOptionDto,
  ColorOptionDto2,
  ColorStepDto,
  ConfigurationDto,
  ConfigurationPrintOptionDto,
  ConfiguratorDto,
  ConfiguratorLayoutEnum,
  getGetConfigurator,
  PrintOptionPerPositionStepDto,
  SelectModeEnum,
  StepEnum,
  VersionCollapsibleStepDto,
  VersionStepDto,
  VisualDesignerDto,
} from '@/api-sdk';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
  printExample,
  PrintExampleContextType,
  visualDesigner,
  VisualDesignerContextType,
} from '@/context';
import {
  MobileController,
  PersonalisationEventController,
  PrintExampleEventController,
  StoreController,
  VisualDesignerEventController,
} from '@/controllers';
import { ActionEvent, GTMEventType, LayoutType, Mode, Step } from '@/types';
import {
  exposeComponentChange,
  getHspEnabled,
  getNewlyAddedColors,
  getParamsObjectFromQueryString,
  haveColorsChanged,
  i18nInit,
  isScrolledIntoView,
  normalizeColors,
  scrollStepIntoView,
  sendColorSelectionChangeEvent,
  sendColorSelectModeChangeEvent,
  sendGTMEvent,
  sendPrintOptionChangeEvent,
  sendPrintOptionNoPrintEvent,
  sendQuantityChangedEvent,
  sendVersionChangedEvent,
  setBaseUrl,
} from '@/utils';
import { goToStep, setScrollDelay, toggleMobileConfigurator } from './utils';

import { FgConfigurator } from '@/components';
import { configuratorStore, ValidationMessages } from '@/stores';

type Constructor<T> = { new (...args: any[]): T };

export const ConfiguratorMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class ConfiguratorMixinClass extends superClass {
    configuratorStore = configuratorStore.getState();

    private static readonly LOADINGSPINNER_DELAY = 250;

    @property({ type: String })
    product = '-1';

    @property({ type: String })
    productName?: string;

    @property({ type: String })
    productImage?: string;

    @property({ type: String })
    locale = 'en_US';

    @property({ type: String })
    color = '';

    @property({ type: String })
    baseUrl = '';

    @property({ type: String })
    placeholder = '';

    @property({ type: String })
    configurationId?: string;

    @property({ type: Array })
    usps: { text: string }[] = [];

    @property({ type: Boolean })
    editing = false;

    @property({ type: String })
    layout: LayoutType = 'wholesale';

    @property({ type: Number })
    colorCollapsible: 0 | 1 = 0;

    @property({ type: Number })
    versionCollapsible: 0 | 1 = 0;

    @property({ type: Number })
    printOptionCollapsible: 0 | 1 = 0;

    @property({ type: Number })
    quantityCollapsible: 0 | 1 = 0;

    @property({ type: String })
    mobileCollapsible: '0' | '1' = '0';

    @property({ type: Number })
    showProductionSpeedStep: 0 | 1 = 1;

    @property({ type: Array })
    selectedPrintOptions?: { code: string }[];

    @property({ type: Boolean })
    enableVisualDesigner = false;

    @property({ type: Object })
    visualDesigner?: VisualDesignerDto;

    /**
     * Debtor is the loggedin reseller ID from CE
     *
     * @property {string} debtor
     */
    @property({ type: String })
    debtor: string | undefined = undefined;

    /**
     * Country is the country from the loggedin reseller from CE
     *
     * @property {string} country
     */
    @property({ type: String })
    country: string | undefined = undefined;

    /**
     * Property which can be set in CE to determine if the product is salable or not
     * depends on the Magento salable return
     */
    @property({ type: Boolean })
    salable = false;

    /**
     * If customer in logged in this value should be true
     */
    @property({ type: Boolean })
    loggedIn = false;

    /**
     * Customer ID is the loggedin customer ID from CE
     */
    @property({ type: Number })
    customerId?: number;

    /**
     * Used to make some links inside of web components that need to link to store specific urls
     */
    @property({ type: String })
    shopUrl = '';

    @property({ type: Number })
    stickySummaryHeight = 0;

    @property({ type: Boolean })
    hideSubtotalZeroPrice = false;

    @state()
    steps: Step[] = [];

    @state()
    configuration?: ConfigurationDto | null;

    @state()
    queryString = '';

    @state()
    isValidConfiguration = false;

    @state()
    isLoading = true;

    @state()
    hasQuantityChanged = false;

    @state()
    error = false;

    @state()
    getGetConfiguratorController: AbortController | null = null;

    @state()
    getGetConfiguratorPromise: Promise<ConfiguratorDto> | null = null;

    @state()
    isConfiguratorLoaded = false;

    activeTimeoutIDs: ReturnType<typeof setTimeout>[] = [];

    @state()
    validSteps: { [key: string]: boolean } = {};

    @state()
    quantityValidated = false;

    @state()
    firstValidationMessage: string | null = '';

    @state()
    activeStep = 0;

    @state()
    toggleMobileConfigurator = false;

    @state()
    unprinted = false;

    @state()
    selectedColor?: {
      image: string;
      color: string;
      selected: boolean;
      thumbImage: string;
    };

    @state()
    eventToSave?: string;

    @state()
    mode: Mode = Mode.DEFAULT;

    /**
     * Track the current color select mode to detect changes
     */
    @state()
    currentColorSelectMode: SelectModeEnum | null = null;

    /**
     * Track the currently selected colors to detect changes
     */
    @state()
    currentSelectedColors: string[] = [];

    /**
     * Track the number of times colors have been switched
     */
    @state()
    switchColorCount: number = 0;

    /**
     * Track the current print option code to detect changes
     */
    @state()
    currentPrintOptionCode: string | null = null;

    @state()
    announcement: string = '';

    @state()
    scrollElementIntoView: string | undefined = undefined;

    @state()
    @provide({ context: global.context })
    globalContext!: GlobalComponentType;

    @state()
    @provide({ context: configuratorGlobal.context })
    configuratorContext!: ConfiguratorContextType;

    @state()
    @provide({ context: configuratorGlobal.resetContext })
    resetConfigurator = false;

    @state()
    @provide({ context: personalisation.context })
    personalisationContext: PersonalisationType[] = [];

    @state()
    @provide({ context: printExample.context })
    printExampleContext?: PrintExampleContextType;

    @state()
    @provide({ context: visualDesigner.context })
    visualDesignerContext!: VisualDesignerContextType;

    @state()
    validationMessages = this.configuratorStore.validationMessages;

    @state()
    showValidationSnackbar = this.configuratorStore.showValidationSnackbar;

    private readonly VATRATE = '0.2100';
    private _vatRate = this.VATRATE;
    set vatRate(val: string) {
      // always fallback to 21% VAT
      this._vatRate = val || this.VATRATE;
    }

    @property({ type: String })
    get vatRate() {
      return this._vatRate;
    }

    protected mobileController = new MobileController(this);

    private validationStoreController = new StoreController(
      this,
      configuratorStore,
      state => {
        this.validationMessages = state.validationMessages;
        this.showValidationSnackbar = state.showValidationSnackbar;
      },
    );

    protected personalisationEventController =
      new PersonalisationEventController(this, this.personalisationContext);

    protected printExampleEventController = new PrintExampleEventController(
      this,
      this.printExampleContext,
    );

    protected visualDesignerEventController = new VisualDesignerEventController(
      this,
      this.visualDesignerContext,
    );

    public configuratorElement: Ref<HTMLElement> = createRef();

    public configuratorWrapper: Ref<HTMLElement> = createRef();

    connectedCallback(): void {
      this.addEventListener(
        'configurator-context-update',
        this.updateConfiguratorContext,
      );

      this.addEventListener('reset-action-event', () => {
        this.eventToSave = undefined;
      });

      this.addEventListener('handle-announcement', this.setAnnouncement);

      super.connectedCallback();
    }

    disconnectedCallback(): void {
      super.disconnectedCallback();

      this.removeEventListener('reset-action-event', () => {
        this.eventToSave = undefined;
      });

      this.removeEventListener('handle-announcement', this.setAnnouncement);
    }

    setAnnouncement(event: CustomEvent) {
      this.announcement = event.detail.announcement;
    }

    updateConfiguratorContext(
      event: CustomEvent<{ context: ConfiguratorContextType }>,
    ) {
      const { context } = event.detail;

      this.configuratorContext = context;
    }

    updateVisualDesignerContext(
      event: CustomEvent<{ context: VisualDesignerContextType }>,
    ) {
      const { context } = event.detail;

      this.visualDesignerContext = context;
    }

    async scheduleUpdate() {
      setBaseUrl(this.baseUrl);

      window.i18next = window.i18next || i18nInit(this.locale);
      await window.i18next;

      super.scheduleUpdate();
    }

    async firstUpdated() {
      this.globalContext = {
        layout: this.layout,
        country: this.country,
        locale: this.locale,
        placeholder: this.placeholder,
        baseUrl: this.baseUrl,
        customerId: this.customerId,
        debtor: this.debtor,
        vatRate: this.vatRate,
        loggedIn: this.loggedIn,
      };

      /**
       * Type casted like this because i need the properties of FgConfigurator, and not he function of ConfiguratorMixin.
       * ConfiguratorMixin is extended by FgConfigurator so it should not be a problem.
       */
      this.configuratorContext = configuratorGlobal.init(
        this as unknown as FgConfigurator,
      );

      const optionParams: ParsedQs = {
        configurationId: this.configuratorContext.configurationId,
      };

      if (this.configuratorContext.color) {
        optionParams.colors = [this.configuratorContext.color];
      }

      await this.getConfigurator({
        optionParams,
      });
    }

    protected willUpdate(
      _changedProperties: PropertyValueMap<ConfiguratorMixinClass>,
    ): void {
      if (_changedProperties.has('steps')) {
        this.updateStepValidationObject();
        const hspEnabled = getHspEnabled(this.steps);
        const printOptionStep = this.steps.find(
          step =>
            step.type === StepEnum.print_option_collapsible ||
            step.type === StepEnum.print_option_per_position,
        ) as PrintOptionPerPositionStepDto | undefined;

        this.configuratorContext = {
          ...this.configuratorContext,
          hspEnabled,
          colorCollapsible: this.colorCollapsible,
          visualDesigner: this.enableVisualDesigner
            ? printOptionStep?.visualDesigner
            : undefined,
        };
      }

      exposeComponentChange(this);

      const { isMobile } = this.mobileController;

      this.personalisationContext = this.personalisationEventController.context;
      this.printExampleContext = this.printExampleEventController.context;
      this.visualDesignerContext = this.visualDesignerEventController.context;

      if (_changedProperties.has('stickySummaryHeight') && !isMobile) {
        this.configuratorContext = {
          ...this.configuratorContext,
          stickySummaryHeight: this.stickySummaryHeight,
        };
      }

      if (_changedProperties.has('hideSubtotalZeroPrice')) {
        this.configuratorContext = {
          ...this.configuratorContext,
          hideSubtotalZeroPrice: this.hideSubtotalZeroPrice,
        };
        this.requestUpdate();
      }
    }

    setMode(event: CustomEvent) {
      const { mode } = event.detail;

      this.configuratorContext = {
        ...this.configuratorContext,
        mode: mode,
      };
    }

    protected updated() {
      this.scrollToStep();
    }

    scrollToStep() {
      const { isMobile } = this.mobileController;

      if (
        isMobile &&
        this.mobileCollapsible === '1' &&
        this.globalContext.layout === 'retail'
      ) {
        const selectedStep = this.findOptimalStep(this.steps);
        const stepElement = stepNamedElements.find(
          step => step.stepType === selectedStep?.type,
        );

        const shadowRoot = this.shadowRoot as ShadowRoot;
        const shadowRootElements = shadowRoot.querySelectorAll('*');
        const scrollElementIntoView = Array.from(shadowRootElements).find(
          (element: Element) =>
            element.tagName.toLowerCase() === stepElement?.element,
        );

        if (this.scrollElementIntoView !== stepElement?.element) {
          scrollStepIntoView(scrollElementIntoView as LitElement);
          isScrolledIntoView(scrollElementIntoView as LitElement);
          this.scrollElementIntoView = stepElement?.element;
        }
      }
    }

    findOptimalStep(steps: Step[]) {
      const isValid = (step: Step) => step?.valid || step?.details?.valid;

      // If first step is not valid, return first step
      if (steps.length > 0 && !isValid(steps[0])) {
        return steps[0];
      }

      // First valid step followed by invalid step
      for (let i = 0; i < steps.length - 1; i++) {
        if (isValid(steps[i]) && !isValid(steps[i + 1])) {
          return steps[i];
        }
      }

      // Fallback: last valid step
      return steps.findLast(step => isValid(step)) || null;
    }

    updateStepValidationObject() {
      this.configuratorContext = {
        ...this.configuratorContext,
        hasValidSteps: Object.values(this.validationMessages).every(Boolean),
      };
    }

    async getConfigurator({ optionParams }: { optionParams?: ParsedQs } = {}) {
      if (!this.configuratorContext.product) {
        return;
      }

      const timerId = setTimeout(() => {
          // delay loading state to prevent flickering
          this.isConfiguratorLoaded = false;
        }, ConfiguratorMixinClass.LOADINGSPINNER_DELAY),
        { layout: contextLayout, locale } = this.globalContext,
        { product, configurationId, colorCollapsible } =
          this.configuratorContext,
        layout =
          contextLayout === 'wholesale'
            ? ConfiguratorLayoutEnum.wholesale
            : ConfiguratorLayoutEnum.retail;

      this.activeTimeoutIDs.push(timerId);
      const { isMobile } = this.mobileController;
      if (this.getGetConfiguratorPromise && this.getGetConfiguratorController) {
        this.getGetConfiguratorController.abort();
      }

      const hasColorInParams =
        !!optionParams?.color ||
        !!optionParams?.colors ||
        (Array.isArray(optionParams?.colors) && optionParams.colors.length > 0);

      this.getGetConfiguratorController = new AbortController();
      this.getGetConfiguratorPromise = getGetConfigurator(
        {
          code: product,
          params: {
            locale: locale,
            layout: layout,
            colorCollapsible: colorCollapsible,
            versionCollapsible: this.versionCollapsible,
            printOptionCollapsible: this.printOptionCollapsible,
            showProductionSpeedStep: this.showProductionSpeedStep,
            quantityCollapsible: this.quantityCollapsible,
            debtor: this.debtor,
            country: this.country,
            ...optionParams,
          },
        },
        {
          signal: this.getGetConfiguratorController.signal,
        },
      );
      try {
        const configuratorData = await this.getGetConfiguratorPromise;
        if (configuratorData) {
          this.isLoading = false;
          this._clearActiveTimeouts();
          this.isConfiguratorLoaded = true;

          const { steps, configuration, unprinted } = configuratorData;

          if (
            !this.configuratorContext.firstGetConfiguratorCall &&
            hasColorInParams
          ) {
            const colorCollapsibleStep = (steps as Step[]).find(
              step => step.type === StepEnum.color_collapsible,
            ) as ColorCollapsibleStepDto | undefined;

            const colorStep = (steps as Step[]).find(
              step => step.type === StepEnum.color,
            ) as ColorStepDto | undefined;

            const colors: (ColorOptionDto2 | ColorOptionDto)[] =
              colorCollapsibleStep?.colorSelections?.[0]?.colors ||
              colorStep?.colors ||
              [];

            const selectedColorOption = colors.find(color => color.selected);

            this.queryString = selectedColorOption?.queryString || '';

            if (colorCollapsibleStep) {
              const colorSelection = colorCollapsibleStep.colorSelections[0];
              if (colorSelection) {
                const imageUrl = colorSelection.selectedColorImage?.main || '';
                if (imageUrl) {
                  this.selectedColor = {
                    image: imageUrl,
                    color: colorSelection.selectedColor || '',
                    selected: true,
                    thumbImage:
                      colorSelection.selectedColorImage?.thumbnail || '',
                  };
                }
              }
            } else if (colorStep && selectedColorOption) {
              const color = selectedColorOption as ColorOptionDto;
              const imageUrl = color.mainImage || '';
              if (imageUrl) {
                this.selectedColor = {
                  image: imageUrl,
                  color: color.color,
                  selected: true,
                  thumbImage: color.thumbnailImage || '',
                };
              }
            }

            if (selectedColorOption?.queryString && !hasColorInParams) {
              const optionParams = getParamsObjectFromQueryString(
                selectedColorOption.queryString,
              );
              this.handleColorOption(optionParams);
            }
          }

          this.steps = steps;
          this.configuration = configuration;
          this.unprinted = unprinted;

          const printOptionPerPositionStep = this.steps.find(
            step =>
              step.type === StepEnum.print_option_collapsible ||
              step.type === StepEnum.print_option_per_position,
          ) as PrintOptionPerPositionStepDto;

          const { isVisualDesignerAvailable, variant, printOptions } =
            printOptionPerPositionStep?.visualDesigner ?? {};

          if (isVisualDesignerAvailable) {
            visualDesigner.providerUpdateEvent(this, {
              ...this.visualDesignerContext,
              productId: this.product,
              productName: this.productName,
              variant,
              printOptions,
              ...(configuration?.printOptions &&
              configuration.printOptions.length > 0
                ? {
                    selectedPrintOptions: configuration.printOptions.map(
                      (printOption: ConfigurationPrintOptionDto) => ({
                        code: printOption.code,
                        colorCount: printOption.colorCount,
                      }),
                    ),
                  }
                : {}),
            });
          }

          if (
            !this.configuratorContext.firstGetConfiguratorCall &&
            hasColorInParams &&
            this.selectedColor
          ) {
            const eventDetail = {
              image: this.selectedColor!.image,
              color: this.selectedColor!.color,
              thumbImage: this.selectedColor!.thumbImage || null,
            };
            this.dispatchEvent(
              new CustomEvent('color-selected', {
                detail: eventDetail,
                composed: true,
              }),
            );
          }
          this.configuratorContext = {
            ...this.configuratorContext,
            firstGetConfiguratorCall: true,
          };
        }
      } catch (error) {
        if (isAxiosError(error) && error?.name === 'CanceledError') {
          return;
        }
        this.isLoading = false;
        this.error = true;
      } finally {
        this.getGetConfiguratorPromise = null;

        if (configurationId && isMobile && !this.toggleMobileConfigurator) {
          this.dispatchEvent(
            new CustomEvent('toggle-configurator', { composed: true }),
          );
          this.toggleConfigurator();
        }
        this.validateMessages();
      }
    }

    onOptionSelected(event: CustomEvent, step: StepEnum) {
      const { queryString, code, filterOptionLayout, subStepIndex } =
        event.detail.option;
      /**
       * Reset configuration ID if it is passed on load, and the configuration quantity is changed. This is to prevent
       * the quantity & price breakdown from not being updated, Which are set via a different API call.
       */
      if (!this.configuratorContext.configurationId || this.editing) {
        this.configuratorContext = {
          ...this.configuratorContext,
          configurationId: undefined,
        };
      }

      this.configuratorStore.setShowValidationSnackbar(false);

      this.queryString = queryString;
      const optionParams = getParamsObjectFromQueryString(queryString);
      if (
        step.includes(StepEnum.quantity) &&
        optionParams?.quantities &&
        !this.hasQuantityChanged
      ) {
        sendQuantityChangedEvent();
        this.hasQuantityChanged = true;
      }

      if (step.includes(StepEnum.print_option)) {
        this.handlePrintOption(
          optionParams,
          code,
          filterOptionLayout,
          subStepIndex,
        );
      } else if (step.includes(StepEnum.color)) {
        this.handleColorOption(optionParams);
      } else if (step.includes(StepEnum.version)) {
        const versionStep = this.steps?.find(step =>
          step?.type?.includes(StepEnum.version),
        ) as VersionCollapsibleStepDto | VersionStepDto;

        const selectedVersion = versionStep?.versions?.find(
          version => version?.queryString === queryString,
        )?.version;

        if (selectedVersion) sendVersionChangedEvent(selectedVersion);
      }

      this.getUpdatedConfiguration(queryString);
    }

    handlePrintOption(
      optionParams: ParsedQs,
      code: string,
      filterOptionLayout: string,
      subStepIndex: number,
    ) {
      if (optionParams.unprinted) {
        sendPrintOptionNoPrintEvent(filterOptionLayout, subStepIndex + 1);
      } else if (code) {
        sendPrintOptionChangeEvent(
          this.currentPrintOptionCode,
          code,
          filterOptionLayout,
          subStepIndex + 1,
        );
        this.currentPrintOptionCode = code;
      }
    }

    handleColorOption(optionParams: ParsedQs) {
      const colorSelectModeChanged =
        this.handleColorSelectModeChange(optionParams);

      if (optionParams.colors !== undefined) {
        const newColors = normalizeColors(optionParams.colors);

        const newlyAddedColors = getNewlyAddedColors(
          this.currentSelectedColors,
          newColors,
        );

        if (newlyAddedColors.length > 0) {
          this.configuratorContext = {
            ...this.configuratorContext,
            color: newlyAddedColors[0],
          };
        }

        if (colorSelectModeChanged) {
          this.currentSelectedColors = newColors;
        } else {
          this.updateColorsIfChanged(newColors);
        }
      }
    }

    handleColorSelectModeChange(optionParams: ParsedQs) {
      if (optionParams.colorSelectMode === null) return false;

      const newColorSelectMode = optionParams.colorSelectMode as SelectModeEnum;
      const changed = sendColorSelectModeChangeEvent(
        this.currentColorSelectMode,
        newColorSelectMode,
      );

      if (changed) {
        this.currentColorSelectMode = newColorSelectMode;
      }

      return changed;
    }

    updateColorsIfChanged(newColors: string[]) {
      const colorsChanged = haveColorsChanged(
        this.currentSelectedColors,
        newColors,
      );
      if (colorsChanged) {
        this.switchColorCount++;
        sendColorSelectionChangeEvent(newColors, this.switchColorCount);
        this.currentSelectedColors = newColors;
      }
    }

    onDefaultColorSelected() {
      this.configuratorContext = {
        ...this.configuratorContext,
        color: '',
      };
    }

    onQuantitySet(event: CustomEvent) {
      const { queryString } = event.detail;
      if (!this.hasQuantityChanged) {
        sendQuantityChangedEvent();
        this.hasQuantityChanged = true;
      }

      /**
       * Reset configuration ID if it is passed on load, and the configuration quantity is changed. This is to prevent
       * the quantity & price breakdown from not being updated, Which are set via a different API call.
       */
      if (this.configuratorContext.configurationId !== '') {
        this.configuratorContext = {
          ...this.configuratorContext,
          configurationId: undefined,
        };
      }

      this.queryString = queryString;

      this.configuratorStore.setShowValidationSnackbar(false);

      this.getUpdatedConfiguration(queryString);
    }

    getUpdatedConfiguration(queryString: string) {
      const optionParams = getParamsObjectFromQueryString(queryString);

      this.getConfigurator({
        optionParams: {
          ...optionParams,
          quantities: Object.assign({}, optionParams.quantities),
        },
      });
    }

    resetConfiguration() {
      this.queryString = '';

      this.configuratorContext = {
        ...this.configuratorContext,
        color: undefined,
      };

      goToStep(this.configuratorElement.value, 0);

      this.activeStep = 0;
      this.hasQuantityChanged = false;
      this.getUpdatedConfiguration(this.queryString);
      this.resetConfigurator = true;
    }

    goToStepOnSelection(element: EventTarget | null = null) {
      const { isMobile } = this.mobileController;

      if (!isMobile) {
        return;
      }

      if (element instanceof HTMLElement && this.mobileCollapsible !== '1') {
        const parent = element.parentElement;

        if (parent) {
          const siblings = Array.from(parent.children);
          const position = siblings.indexOf(element);

          this.activeStep = position;

          goToStep(
            this.configuratorElement.value,
            this.activeStep,
            setScrollDelay(this.steps[this.activeStep]?.type || null),
          );

          element.focus();
        }
      }
    }

    private _clearActiveTimeouts() {
      while (this.activeTimeoutIDs.length > 0) {
        const timerId = this.activeTimeoutIDs.pop();
        if (timerId) {
          clearTimeout(timerId);
        }
      }
    }

    validateMessages() {
      const { setValidationMessages } = this.configuratorStore;
      const validationMessages = this.steps.reduce<ValidationMessages>(
        (messages, step) => ({
          ...messages,
          [step.type]: step.validationMessage ?? undefined,
        }),
        {},
      );

      setValidationMessages(validationMessages);
    }

    validateQuantity(
      event: CustomEvent<{ step: StepEnum; validationMessage: string | null }>,
    ) {
      const { step, validationMessage } = event.detail;

      const configuratorStep = this.steps?.find(({ type }) => type === step);
      if (configuratorStep) {
        configuratorStep.validationMessage = validationMessage;
      }

      this.quantityValidated = validationMessage === null;
      this.validateMessages();
    }

    /**
     * Custom event triggered in the fg-configurator-quantity-variants, to check if one of the quantity inputs is over
     * the stock limit. This will prevent the add to cart/update cart button form being triggered when the quantity is
     * higher than the stock limit.
     *
     * @param event
     */
    validateStockPerVariant(event: CustomEvent<{ valid: boolean }>) {
      this.quantityValidated =
        event.detail.valid &&
        !Object.values(this.validationMessages).some(Boolean);
    }

    toggleAllStepsValidationMessages(
      event: CustomEvent<{ actionEvent: ActionEvent }>,
    ) {
      const { setShowValidationMessage } = this.configuratorStore;
      const { actionEvent } = event.detail;

      this.configuratorStore.setShowValidationSnackbar(true);

      this.firstValidationMessage =
        Object.entries(this.validationMessages).find(
          ([, message]) => message,
        )?.[0] ?? null;

      setShowValidationMessage();

      Object.entries(this.validationMessages).forEach(([key, message]) => {
        if (!message) {
          sendGTMEvent(GTMEventType.ERROR_EVENT, {
            error_type: 'product_configurator',
            error_message: this.steps.find(step => step.type === key)
              ?.validationMessage,
            button: actionEvent,
          });
        }
      });

      if (actionEvent !== 'add-to-cart' && actionEvent !== 'update-cart') {
        this.dispatchEvent(
          new CustomEvent('trigger-validation-information', {
            composed: true,
            detail: {
              steps: this.validationMessages,
              actionEvent: actionEvent,
            },
          }),
        );
      }
    }

    scrollToValidationMessage(step: StepEnum) {
      if (step === this.firstValidationMessage) {
        return true;
      }
      return false;
    }

    resetScrollValidationMessage() {
      this.firstValidationMessage = null;
    }

    async toggleConfigurator() {
      /**
       * wait for the configurator to be rendered completely before toggling the configurator, this prevents the
       * configuratorWrapper to be null
       */
      await this.updateComplete;

      this.toggleMobileConfigurator = toggleMobileConfigurator(
        this.toggleMobileConfigurator,
        this.configuratorWrapper.value,
        this.mobileController.isMobile,
      );
    }
  }

  return ConfiguratorMixinClass;
};
